import React, { useState, useEffect, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../common/LoadingSpinner.jsx';
import { searchAddressByPostalCode, formatPostalCode } from '../../../utils/postalCode.js';
import { buildApiUrl } from '../../../utils/apiBase.js';
import { assertAuthApiOk } from '../../../utils/authApiErrors.js';
import { validatePassword } from '../../../utils/password.js';
import { useBeta } from '../../../contexts/BetaContext.jsx';
import { loadRecaptchaScript, getRecaptchaToken } from '../../../utils/recaptcha.js';
import HubAuthShell from './HubAuthShell.jsx';

/**
 * 新規クリニック登録ページ
 */
const SignupPage = () => {
  const { isBetaPeriod, isHubSettingsReady } = useBeta();
  const [step, setStep] = useState(1); // 1: クリニック情報, 2: 管理者情報, 3: 確認
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchingAddress, setSearchingAddress] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const navigate = useNavigate();

  // クリニック情報
  const [clinicData, setClinicData] = useState({
    name: '',
    postalCode: '',
    address: '',
    phone: '',
    email: '',
    contactPerson: ''
  });

  // 管理者情報
  const [adminData, setAdminData] = useState({
    userId: '',
    displayName: '',
    password: '',
    passwordConfirm: ''
  });

  // ステップ3: 利用規約・プライバシーポリシー同意
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);

  // β版告知ポップアップ（β期間中のみ、画面表示時に1回表示）
  const [betaNoticeDismissed, setBetaNoticeDismissed] = useState(false);
  const showBetaNotice = isHubSettingsReady && isBetaPeriod === true && !betaNoticeDismissed;

  useEffect(() => {
    loadRecaptchaScript();
  }, []);

  // 郵便番号の入力ハンドラー
  const handlePostalCodeChange = async (e) => {
    // 全角数字を半角に変換
    let value = e.target.value.replace(/[０-９]/g, (s) => {
      return String.fromCharCode(s.charCodeAt(0) - 0xFEE0);
    });
    
    // 全角ハイフンを半角に変換
    value = value.replace(/[－−]/g, '-');
    
    const formatted = formatPostalCode(value);
    const normalizedCode = formatted.replace(/[^\d]/g, '');
    
    setClinicData(prev => ({ ...prev, postalCode: formatted }));

    if (normalizedCode.length === 7) {
      setSearchingAddress(true);
      try {
        const result = await searchAddressByPostalCode(formatted);
        if (result.success) {
          setClinicData(prev => ({ ...prev, address: result.address }));
        }
      } catch (error) {
        console.error('住所検索エラー:', error);
      } finally {
        setSearchingAddress(false);
      }
    }
  };

  // ステップ1のバリデーション
  const validateStep1 = () => {
    if (!clinicData.name.trim()) {
      setError('クリニック名を入力してください。');
      return false;
    }
    if (!clinicData.address.trim()) {
      setError('住所を入力してください。');
      return false;
    }
    if (!clinicData.email.trim()) {
      setError('メールアドレスを入力してください。');
      return false;
    }
    if (!clinicData.contactPerson.trim()) {
      setError('担当者名を入力してください。');
      return false;
    }
    return true;
  };

  // ステップ2のバリデーション
  const validateStep2 = () => {
    if (!adminData.userId.trim()) {
      setError('ユーザーIDを入力してください。');
      return false;
    }
    if (adminData.userId.includes('@')) {
      setError('ユーザーIDに@は使用できません。');
      return false;
    }
    if (!adminData.displayName.trim()) {
      setError('ユーザー名を入力してください。');
      return false;
    }
    const pwdCheck = validatePassword(adminData.password);
    if (!pwdCheck.valid) {
      setError(pwdCheck.message || 'パスワードを入力してください。');
      return false;
    }
    if (adminData.password !== adminData.passwordConfirm) {
      setError('パスワードが一致しません。');
      return false;
    }
    return true;
  };

  // 次へボタン
  const handleNext = () => {
    setError('');
    if (step === 1 && validateStep1()) {
      setIsTransitioning(true);
      setTimeout(() => {
        setStep(2);
        setTimeout(() => {
          setIsTransitioning(false);
        }, 50);
      }, 300); // フェードアウト時間
    }
  };

  // 戻るボタン
  const handleBack = () => {
    setError('');
    setIsTransitioning(true);
    setTimeout(() => {
      setStep(step - 1);
      setTimeout(() => {
        setIsTransitioning(false);
      }, 50);
    }, 300); // フェードアウト時間
  };

  // 確認画面へ
  const handleConfirm = () => {
    setError('');
    if (!validateStep2()) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setStep(3);
      setTimeout(() => {
        setIsTransitioning(false);
      }, 50);
    }, 300);
  };

  // 登録実行（確認画面からのみ）
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!agreedToTerms || !agreedToPrivacy) {
      setError('利用規約とプライバシーポリシーに同意のうえ、両方のチェックを入れてください。');
      return;
    }

    setLoading(true);
    try {
      const recaptchaToken = await getRecaptchaToken('signup');
      const response = await fetch(buildApiUrl('/api/auth/signup'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          clinic: clinicData,
          admin: {
            userId: adminData.userId,
            displayName: adminData.displayName,
            password: adminData.password
          },
          agreedToTerms,
          agreedToPrivacy,
          recaptchaToken,
        })
      });

      const data = await response.json();

      if (!response.ok) {
        assertAuthApiOk(response, data, 'クリニックの登録に失敗しました。');
        throw new Error(data.message || 'クリニックの登録に失敗しました。');
      }

      navigate('/verify-email/pending', {
        replace: true,
        state: {
          fromSignup: true,
          clinicId: data.clinicId,
          clinicName: clinicData.name,
          clinicCode: data.code || '',
          userId: adminData.userId,
          email: clinicData.email,
          verificationEmailSent: data.verificationEmailSent !== false,
          signupMessage: data.message || '',
        },
      });
      return;
    } catch (err) {
      console.error('登録エラー:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const betaNotice = showBetaNotice ? (
    <div
      role="dialog"
      aria-labelledby="beta-notice-title"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--hub-space-lg)',
        background: 'rgba(0, 0, 0, 0.5)',
        boxSizing: 'border-box',
      }}
      onClick={() => setBetaNoticeDismissed(true)}
    >
      <div
        className="hub-auth-card"
        style={{
          maxWidth: '440px',
          width: '100%',
          background: 'var(--hub-bg-primary)',
          boxShadow: 'var(--hub-shadow-xl)',
          borderRadius: 'var(--hub-radius-lg)',
          padding: 'var(--hub-space-xl)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="beta-notice-title"
          className="hub-auth-title"
          style={{ fontSize: '1.25rem', marginBottom: 'var(--hub-space-lg)' }}
        >
          β版のご利用について
        </h2>
        <ul style={{
          margin: 0,
          paddingLeft: '1.25rem',
          color: 'var(--hub-text-secondary)',
          lineHeight: 1.7,
          marginBottom: 'var(--hub-space-xl)',
        }}>
          <li style={{ marginBottom: 'var(--hub-space-sm)' }}>
            現在は正式リリース前のβテスト期間中です。正式版とは仕様が異なる場合があります。
          </li>
          <li>
            本β版で作成したアカウントのデータは、正式リリース後も引き続きご利用いただけます。
          </li>
        </ul>
        <button
          type="button"
          className="hub-btn hub-btn-gradient"
          style={{ width: '100%' }}
          onClick={() => setBetaNoticeDismissed(true)}
        >
          了解
        </button>
      </div>
    </div>
  ) : null;

  return (
    <HubAuthShell
      revealDeps={[step]}
      cardClassName="hub-auth-card--allow-overflow"
      containerStyle={{ maxWidth: '520px' }}
      beforeContainer={betaNotice}
      outside={(
        <div style={{ textAlign: 'center', marginTop: 'var(--hub-space-xl)' }}>
          <Link
            to="/"
            className="hub-auth-back"
            style={{
              color: 'rgba(255,255,255,0.7)',
              fontSize: '0.875rem',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}>
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            トップページに戻る
          </Link>
        </div>
      )}
    >
          <div className="hub-auth-header">
            <div className="hub-auth-logo">
              <img src="/logo.webp" alt="Synapse" style={{ width: 48, height: 48, objectFit: 'contain' }} />
            </div>
            <h1 className="hub-auth-title">新規登録</h1>
            <p className="hub-auth-subtitle">
              {step === 1 ? 'クリニック情報を入力' : step === 2 ? '管理者アカウントを作成' : '入力内容の確認'}
            </p>
          </div>

          {/* ステップインジケーター */}
          <div style={{ 
            display: 'flex',
            gap: 'var(--hub-space-sm)',
            marginBottom: 'var(--hub-space-xl)'
          }}>
            <div style={{
              flex: 1,
              height: '4px',
              borderRadius: '2px',
              background: 'var(--hub-accent-primary)'
            }} />
            <div className="signup-indicator-bar" style={{
              flex: 1,
              height: '4px',
              borderRadius: '2px',
              background: step >= 2 ? 'var(--hub-accent-primary)' : 'var(--hub-border)',
              transition: 'background-color 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
            }} />
            <div className="signup-indicator-bar" style={{
              flex: 1,
              height: '4px',
              borderRadius: '2px',
              background: step >= 3 ? 'var(--hub-accent-primary)' : 'var(--hub-border)',
              transition: 'background-color 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
            }} />
          </div>

          {error && (
            <div className="hub-auth-error">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="9"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={
            step === 1
              ? (e) => { e.preventDefault(); handleNext(); }
              : step === 2
                ? (e) => { e.preventDefault(); handleConfirm(); }
                : handleSubmit
          } style={{ position: 'relative', minHeight: '400px' }}>
            {/* ステップ1: クリニック情報 */}
            <div className={`signup-step-content ${step === 1 ? 'active' : ''} ${isTransitioning && step === 1 ? 'fade-out' : ''}`}>
              <div className="hub-form-group signup-form-item">
                <label className="hub-form-label hub-form-required">クリニック名</label>
                <input
                  type="text"
                  className="hub-form-input"
                  value={clinicData.name}
                  onChange={(e) => setClinicData({ ...clinicData, name: e.target.value })}
                  placeholder="例: 東京デンタルクリニック"
                  required={step === 1}
                  disabled={step !== 1}
                />
              </div>

              <div className="hub-form-group signup-form-item">
                <label className="hub-form-label">
                  郵便番号
                  {searchingAddress && (
                    <span style={{ marginLeft: '8px', color: 'var(--hub-accent-primary)', fontSize: '0.875rem' }}>
                      （住所を検索中...）
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  className="hub-form-input"
                  value={clinicData.postalCode}
                  onChange={handlePostalCodeChange}
                  placeholder="例: 100-0001"
                  maxLength="8"
                  inputMode="numeric"
                  pattern="[0-9\-]*"
                  style={{ imeMode: 'disabled' }}
                  disabled={step !== 1}
                />
                <span className="hub-form-hint">7桁入力で住所を自動検索します</span>
              </div>

              <div className="hub-form-group signup-form-item">
                <label className="hub-form-label hub-form-required">住所</label>
                <input
                  type="text"
                  className="hub-form-input"
                  value={clinicData.address}
                  onChange={(e) => setClinicData({ ...clinicData, address: e.target.value })}
                  placeholder="例: 東京都渋谷区〇〇1-2-3"
                  required={step === 1}
                  disabled={step !== 1}
                />
              </div>

              <div className="hub-form-group signup-form-item">
                <label className="hub-form-label">電話番号</label>
                <input
                  type="tel"
                  className="hub-form-input"
                  value={clinicData.phone}
                  onChange={(e) => setClinicData({ ...clinicData, phone: e.target.value })}
                  placeholder="例: 03-1234-5678"
                  disabled={step !== 1}
                />
              </div>

              <div className="hub-form-group signup-form-item">
                <label className="hub-form-label hub-form-required">メールアドレス</label>
                <input
                  type="email"
                  className="hub-form-input"
                  value={clinicData.email}
                  onChange={(e) => setClinicData({ ...clinicData, email: e.target.value })}
                  placeholder="例: info@clinic.example.com"
                  required={step === 1}
                  disabled={step !== 1}
                />
              </div>

              <div className="hub-form-group signup-form-item">
                <label className="hub-form-label hub-form-required">担当者名</label>
                <input
                  type="text"
                  className="hub-form-input"
                  value={clinicData.contactPerson}
                  onChange={(e) => setClinicData({ ...clinicData, contactPerson: e.target.value })}
                  placeholder="例: 山田 太郎"
                  required={step === 1}
                  disabled={step !== 1}
                />
              </div>

              <button
                type="submit"
                className="hub-btn hub-btn-gradient"
                style={{ width: '100%' }}
              >
                次へ
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="5" y1="12" x2="19" y2="12"/>
                  <polyline points="12 5 19 12 12 19"/>
                </svg>
              </button>
            </div>

            {/* ステップ2: 管理者情報 */}
            <div className={`signup-step-content ${step === 2 ? 'active' : ''} ${isTransitioning && step === 2 ? 'fade-out' : ''}`}>
              <div className="hub-form-group signup-form-item">
                <label className="hub-form-label hub-form-required">ユーザーID</label>
                <input
                  type="text"
                  className="hub-form-input"
                  value={adminData.userId}
                  onChange={(e) => setAdminData({ ...adminData, userId: e.target.value })}
                  placeholder="例: yamada（半角英数字）"
                  required={step === 2}
                  disabled={step !== 2}
                />
                <span className="hub-form-hint">ログイン時に使用します</span>
              </div>

              <div className="hub-form-group signup-form-item">
                <div className="hub-form-label-help">
                  <label className="hub-form-label hub-form-required">ユーザー名</label>
                  <span className="hub-help-tooltip">
                    <button
                      type="button"
                      className="hub-help-tooltip-trigger"
                      aria-label="ユーザー名のヒントを表示"
                    >
                      i
                    </button>
                    <span className="hub-help-tooltip-content">
                      初回アカウントには自動的に医師の権限が付与されます。<br />
                      主に管理を行う担当医師の表示名を登録してください。
                    </span>
                  </span>
                </div>
                <input
                  type="text"
                  className="hub-form-input"
                  value={adminData.displayName}
                  onChange={(e) => setAdminData({ ...adminData, displayName: e.target.value })}
                  placeholder="例: 山田 太郎"
                  required={step === 2}
                  disabled={step !== 2}
                />
              </div>

              <div className="hub-form-group signup-form-item">
                <label className="hub-form-label hub-form-required">パスワード</label>
                <input
                  type="password"
                  className="hub-form-input"
                  value={adminData.password}
                  onChange={(e) => setAdminData({ ...adminData, password: e.target.value })}
                  placeholder="8文字以上・英数字記号"
                  required={step === 2}
                  minLength={step === 2 ? 8 : undefined}
                  disabled={step !== 2}
                />
              </div>

              <div className="hub-form-group signup-form-item">
                <label className="hub-form-label hub-form-required">パスワード（確認）</label>
                <input
                  type="password"
                  className="hub-form-input"
                  value={adminData.passwordConfirm}
                  onChange={(e) => setAdminData({ ...adminData, passwordConfirm: e.target.value })}
                  placeholder="もう一度入力"
                  required={step === 2}
                  disabled={step !== 2}
                />
              </div>

              <div style={{ display: 'flex', gap: 'var(--hub-space-md)' }}>
                <button
                  type="button"
                  onClick={handleBack}
                  className="hub-btn hub-btn-secondary"
                  style={{ flex: 1 }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="19" y1="12" x2="5" y2="12"/>
                    <polyline points="12 19 5 12 12 5"/>
                  </svg>
                  戻る
                </button>
                <button
                  type="submit"
                  className="hub-btn hub-btn-gradient"
                  style={{ flex: 2 }}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <LoadingSpinner size="small" />
                      確認中...
                    </>
                  ) : (
                    '確認する'
                  )}
                </button>
              </div>
            </div>

            {/* ステップ3: 確認 */}
            <div className={`signup-step-content ${step === 3 ? 'active' : ''} ${isTransitioning && step === 3 ? 'fade-out' : ''}`}>
              <div style={{
                background: 'var(--hub-bg-secondary)',
                borderRadius: 'var(--hub-radius-md)',
                padding: 'var(--hub-space-lg)',
                marginBottom: 'var(--hub-space-xl)'
              }}>
                <div style={{ marginBottom: 'var(--hub-space-md)' }}>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--hub-text-muted)', marginBottom: '4px' }}>
                    クリニック名
                  </div>
                  <div style={{ fontWeight: 600 }}>{clinicData.name}</div>
                </div>
                <div style={{ marginBottom: 'var(--hub-space-md)' }}>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--hub-text-muted)', marginBottom: '4px' }}>
                    住所
                  </div>
                  <div style={{ fontWeight: 600 }}>{clinicData.address}</div>
                </div>
                <div style={{ marginBottom: 'var(--hub-space-md)' }}>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--hub-text-muted)', marginBottom: '4px' }}>
                    郵便番号
                  </div>
                  <div style={{ fontWeight: 600 }}>{clinicData.postalCode || '-'}</div>
                </div>
                <div style={{ marginBottom: 'var(--hub-space-md)' }}>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--hub-text-muted)', marginBottom: '4px' }}>
                    電話番号
                  </div>
                  <div style={{ fontWeight: 600 }}>{clinicData.phone || '-'}</div>
                </div>
                <div style={{ marginBottom: 'var(--hub-space-md)' }}>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--hub-text-muted)', marginBottom: '4px' }}>
                    クリニックメールアドレス
                  </div>
                  <div style={{ fontWeight: 600 }}>{clinicData.email}</div>
                </div>
                <div style={{ marginBottom: 'var(--hub-space-md)' }}>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--hub-text-muted)', marginBottom: '4px' }}>
                    担当者名
                  </div>
                  <div style={{ fontWeight: 600 }}>{clinicData.contactPerson}</div>
                </div>
                <div style={{ marginBottom: 'var(--hub-space-md)' }}>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--hub-text-muted)', marginBottom: '4px' }}>
                    ユーザーID
                  </div>
                  <div style={{ fontWeight: 600 }}>{adminData.userId}</div>
                </div>
                <div style={{ marginBottom: 'var(--hub-space-md)' }}>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--hub-text-muted)', marginBottom: '4px' }}>
                    ユーザー名
                  </div>
                  <div style={{ fontWeight: 600 }}>{adminData.displayName}</div>
                </div>
              </div>

              <div style={{
                background: 'var(--hub-bg-secondary)',
                borderRadius: 'var(--hub-radius-md)',
                padding: 'var(--hub-space-lg)',
                marginBottom: 'var(--hub-space-lg)'
              }}>
                <p style={{ fontSize: '0.875rem', color: 'var(--hub-text-secondary)', marginBottom: 'var(--hub-space-md)' }}>
                  以下の内容に同意のうえ、チェックを入れてください。
                </p>
                <label className="hub-form-check-wrap" style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--hub-space-sm)', marginBottom: 'var(--hub-space-md)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    style={{ marginTop: '4px', flexShrink: 0 }}
                    disabled={step !== 3}
                  />
                  <span style={{ fontSize: '0.9375rem' }}>
                    <Link to="/terms" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: 'var(--hub-accent-primary)', textDecoration: 'underline' }}>
                      利用規約
                    </Link>
                    に同意する
                  </span>
                </label>
                <label className="hub-form-check-wrap" style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--hub-space-sm)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={agreedToPrivacy}
                    onChange={(e) => setAgreedToPrivacy(e.target.checked)}
                    style={{ marginTop: '4px', flexShrink: 0 }}
                    disabled={step !== 3}
                  />
                  <span style={{ fontSize: '0.9375rem' }}>
                    <Link to="/privacy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: 'var(--hub-accent-primary)', textDecoration: 'underline' }}>
                      プライバシーポリシー
                    </Link>
                    に同意する
                  </span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: 'var(--hub-space-md)' }}>
                <button
                  type="button"
                  onClick={handleBack}
                  className="hub-btn hub-btn-secondary"
                  style={{ flex: 1 }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="19" y1="12" x2="5" y2="12"/>
                    <polyline points="12 19 5 12 12 5"/>
                  </svg>
                  戻る
                </button>
                <button
                  type="submit"
                  className="hub-btn hub-btn-gradient"
                  style={{ flex: 2 }}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <LoadingSpinner size="small" />
                      登録中...
                    </>
                  ) : (
                    '登録する'
                  )}
                </button>
              </div>
            </div>
          </form>

          <div className="hub-auth-footer">
            <p>
              すでにアカウントをお持ちの方は<br />
              <Link to="/login">ログイン</Link>
            </p>
          </div>
    </HubAuthShell>
  );
};

export default SignupPage;

